import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { useDropzone } from "react-dropzone";
import {
  Divider,
  Grid,
  makeStyles,
  Button,
  Box,
  Typography
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import BackupTwoToneIcon from "@material-ui/icons/BackupTwoTone";

import { remCalc } from "../../utils/helpers";
import XlsxExampleFile from "../data/import/sample-stops.xlsx";
import xlsxIcon from "../../assets/images/icons/xlsx-icon.svg";

const worker = new Worker("/workers/readExcel.js");

const useStyles = makeStyles(theme => ({
  dropZone: {
    fontSize: 12,
    boxSizing: "border-box",
    borderRadius: 4,
    border: "1px dashed #C2C3C7",
    transition: "border 0.3s, transform 0.2s",
    cursor: "pointer",
    padding: theme.spacing(4.5),
    position: "relative"
  },
  dropZoneDragging: {
    borderColor: "rgba(19,41,254, 0.6)"
  },
  dropHereWrapper: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(52,116,226, 0.25)",
    visibility: "hidden",
    opacity: 0,
    zIndex: 5,
    transition: "all 0.4s"
  },
  dropHereWrapperActive: {
    visibility: "visible",
    opacity: 1
  },
  button: {
    backgroundColor: theme.palette.primary.main,
    textTransform: "none",
    padding: theme.spacing(1, 4),
    height: remCalc(36),
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark
    }
  },
  acceptedExtensionsText: {
    color: "#7D7E82",
    fontSize: theme.typography.pxToRem(15),
    marginTop: theme.spacing(2.25)
  },
  divider: {
    backgroundColor: "#C2C3C7",
    margin: theme.spacing(0, 5)
  },
  columnsDescriptionText: {
    color: "#7D7E82",
    fontSize: theme.typography.pxToRem(16)
  },
  templateFileText: {
    color: "#7D7E82",
    fontSize: theme.typography.pxToRem(14),
    marginTop: theme.spacing(2.5)
  }
}));

function DragAndDrop({ handleFetch, setButtonVisible, type }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [dragEntersWindow, setDragEntersWindow] = useState(false);

  const onDrop = (acceptedFiles, allFiles) => {
    if (acceptedFiles.length) {
      const dataFile = acceptedFiles[0];
      const reader = new FileReader();
      reader.onload = function (e) {
        worker.postMessage({ msg: "read", d: e.target.result, type });
        const splittedName = dataFile.name.split(".");
        const extension = splittedName[splittedName.length - 1];
        worker.onmessage = msg => workerMessage(msg, extension);
      };
      reader.readAsArrayBuffer(dataFile);
    } else if (allFiles.length > 1)
      enqueueSnackbar("Please upload single file");
    else enqueueSnackbar("File type not supported");
  };

  const workerMessage = (e, extension) => {
    if (e && e.data) {
      setButtonVisible && setButtonVisible(true);
      if (e.data.blank) {
        handleFetch({ blank: e.data.blank });
      } else if (e.data.rawData) {
        handleFetch({
          file: btoa(e.data.rawData),
          rawData: e.data.rawData,
          data: e.data.data,
          extension
        });
      } else {
        handleFetch({ body: e.data, rawData: e.data.rawData });
      }
    }
  };

  useEffect(() => {
    const dragStartFunction = function () {
      setDragEntersWindow(true);
    };
    const dragDoneFunction = function (event) {
      if (!event.fromElement) {
        setDragEntersWindow(false);
      }
    };
    window.addEventListener("dragenter", dragStartFunction);
    window.addEventListener("dragleave", dragDoneFunction);
    window.addEventListener("dragend", dragDoneFunction);
    window.addEventListener("drop", dragDoneFunction);

    return () => {
      window.removeEventListener("dragenter", dragStartFunction);
      window.removeEventListener("dragleave", dragDoneFunction);
      window.removeEventListener("dragend", dragDoneFunction);
      window.removeEventListener("drop", dragDoneFunction);
    };
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: ".xls, .xlsx, .csv"
  });
  return (
    <>
      <Grid
        container
        wrap="nowrap"
        alignItems="center"
        className={clsx(classes.dropZone, {
          [classes.dropZoneDragging]: isDragActive
        })}
        {...getRootProps()}
      >
        <div
          className={clsx(classes.dropHereWrapper, {
            [classes.dropHereWrapperActive]: dragEntersWindow
          })}
        >
          <Grid
            container
            style={{ width: "100%", height: "100%" }}
            direction="column"
            alignItems="center"
            alignContent="center"
            justify="flex-end"
          >
            <Grid item>
              <BackupTwoToneIcon style={{ color: "#3474E2", fontSize: 50 }} />
            </Grid>
            <Grid item>
              <Typography
                style={{
                  color: "#FFF",
                  fontSize: 14,
                  fontWeight: 500,
                  backgroundColor: "#3474E2",
                  padding: "5px 10px",
                  marginBottom: 10
                }}
              >
                Drop Here
              </Typography>
            </Grid>
          </Grid>
        </div>
        <input {...getInputProps()} />
        <Grid item style={{ width: 320 }}>
          <Button className={classes.button} data-cy="browse-uploadStops">
            Upload data from file
          </Button>
          <Typography className={classes.acceptedExtensionsText}>
            .cvs, .xls, .xlsx speedsheets accepted.
          </Typography>
        </Grid>
        <Divider
          flexItem
          orientation="vertical"
          variant="middle"
          className={classes.divider}
        />
        <Grid item>
          <Typography className={classes.columnsDescriptionText}>
            Columns should be in the correct order and fields marked in * are
            required (in case lat and long are not defined).
          </Typography>
          <Typography className={classes.templateFileText}>
            Template file:{" "}
            <a
              color={"#1329FE"}
              data-cy="stopsTemplate"
              href={XlsxExampleFile}
              download
              style={{ textDecoration: "none" }}
              onClick={event => {
                event.stopPropagation();
              }}
            >
              <Box
                component="img"
                src={xlsxIcon}
                alt="XLSX"
                style={{ verticalAlign: -6, marginLeft: 8, marginRight: 6 }}
              />
              .XLSX
            </a>
          </Typography>
        </Grid>
      </Grid>
    </>
  );
}

export default DragAndDrop;
